import "./search-alphabetical-picker.scss";
import "design-system/components/search/archive-page-facets/archive-page-facets.scss";

import {
  useState,
  useEffect,
  useId,
  useMemo,
  useRef,
  MouseEvent,
  KeyboardEvent,
} from "react";
import * as Checkbox from "@radix-ui/react-checkbox";
import * as Collapsible from "@radix-ui/react-collapsible";
import { useBreakpoint } from "design-system/hooks/use-breakpoint";
import { Icon } from "design-system/components/icons/icon";
import { CtaLink } from "design-system/components/primitives/cta-link/cta-link";
import cn from "clsx";
import {
  ArchivePageFacetItem,
  ArchivePageFacetsProps,
} from "design-system/components/search/archive-page-facets/archive-page-facets";

/**
 * ## Implementation Notes
 * - This component utilizes the [Radix Checkbox](https://www.radix-ui.com/docs/primitives/components/checkbox) and [Radix Collapsible](https://www.radix-ui.com/docs/primitives/components/collapsible) components
 *
 * ## See it in use on...
 * - An [archive page](/story/example-pages-archive-pages-person-archive--story)
 *
 */

export function SearchAlphabeticalPicker({
  items = [],
  title,
  onFacetChanged,
}: ArchivePageFacetsProps) {
  const INITIAL_ITEMS_LENGTH = 5;

  const sortedItems = useMemo(() => {
    return [...items].sort((a) => (a.checked === true ? -1 : 1));
  }, [items]);

  const { isDesktop } = useBreakpoint();
  const facetsID = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [shownItemLength, setShownItemLength] = useState(INITIAL_ITEMS_LENGTH);
  const [letter, setLetter] = useState<string>("");
  const [activeLetter, setActiveLetter] = useState<string>("");
  const moreRef = useRef<HTMLAnchorElement & HTMLButtonElement>(null);
  const itemsRef = useRef<HTMLAnchorElement & HTMLButtonElement>(null);
  const setFocusAndLength = () => {
    const parentSiblingElement = moreRef.current?.parentNode
      ?.previousSibling as HTMLElement;
    if (parentSiblingElement) {
      const childButton = parentSiblingElement.querySelector("button");
      if (childButton) childButton.focus();
    }
    setShownItemLength(items.length);
  };
  const [truncatedItems, setTruncatedItems] = useState<
    Array<ArchivePageFacetItem>
  >(sortedItems.slice(0, 5));

  const itemsSet = new Set(
    sortedItems.map((item) => item.name?.charAt(0).toLowerCase() ?? ""),
  );

  const alphabetArr = [
    "a",
    "b",
    "c",
    "d",
    "e",
    "f",
    "g",
    "h",
    "i",
    "j",
    "k",
    "l",
    "m",
    "n",
    "o",
    "p",
    "q",
    "r",
    "s",
    "t",
    "u",
    "v",
    "w",
    "x",
    "y",
    "z",
  ];

  const dynamicLength = (letter: string) => {
    return sortedItems.filter((item) => {
      return (item.value?.charAt(0)?.toLowerCase() ?? "") === letter;
    }).length;
  };

  const hideItems = (item: ArchivePageFacetItem) => {
    return (item.value?.charAt(0)?.toLowerCase() ?? "") === letter;
  };

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    setLetter(e.currentTarget.textContent?.toLowerCase() ?? "");
    setActiveLetter(e.currentTarget.textContent?.toLowerCase() ?? "");
    itemsRef.current?.focus();
  };

  const handleEnter = (e: KeyboardEvent<HTMLDivElement>) => {
    if (
      e.key === "Enter" ||
      e.key === " " ||
      e.key === "Spacebar" ||
      e.key === "Space"
    ) {
      setLetter(e.currentTarget.textContent?.toLowerCase() ?? "");
      setActiveLetter(e.currentTarget.textContent?.toLowerCase() ?? "");
      itemsRef.current?.focus();
    }
  };

  useEffect(() => {
    if (isDesktop) {
      setIsOpen(true);
    }
    return () => setIsOpen(false);
  }, [isDesktop]);

  useEffect(() => {
    const alphabetFilter = (letter: string) => {
      return sortedItems.filter((item) => {
        return (item.value?.charAt(0)?.toLowerCase() ?? "") === letter;
      });
    };
    setTruncatedItems(alphabetFilter(letter).slice(0, shownItemLength));
  }, [letter, shownItemLength, sortedItems]);

  const onCheckedChange = (
    checked: boolean | string,
    item: ArchivePageFacetItem,
  ) => {
    if (onFacetChanged) {
      if (typeof checked === "boolean") {
        onFacetChanged(item, checked);
      } else if (typeof checked === "string" && checked === "indeterminate") {
        onFacetChanged(item, false);
      }
    }
  };

  if (sortedItems.length === 0) return null;

  return (
    <Collapsible.Root
      className="hbs-facets"
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <fieldset>
        <Collapsible.Trigger className="hbs-facets__accordion-header-button">
          <legend className="hbs-facets__title">{title}</legend>
          <Icon name="caretDown" className="hbs-facets__caret" />
        </Collapsible.Trigger>
        <Collapsible.Content className="hbs-facets__accordion-menu">
          <div className="hbs-facets__alphabetical-picker-wrapper">
            {alphabetArr.map((letter) => (
              <div
                className={cn(
                  itemsSet.has(letter)
                    ? cn(
                        activeLetter === letter
                          ? "hbs-facets__alphabetical-picker-letter active"
                          : "hbs-facets__alphabetical-picker-letter",
                      )
                    : "hbs-facets__alphabetical-picker-no-letter",
                )}
                tabIndex={0}
                role="button"
                onClick={handleClick}
                onKeyDown={handleEnter}
                key={letter}
                id={letter}
              >
                <strong>{letter.toUpperCase()}</strong>
              </div>
            ))}
          </div>
          <div
            className={
              letter === ""
                ? cn(
                    "hbs-global-visually-hidden",
                    "hbs-facets__alphabetical-picker-items",
                  )
                : "hbs-facets__alphabetical-picker-items"
            }
          >
            {truncatedItems.map((item, i) => (
              <div
                className={
                  hideItems(item)
                    ? "hbs-facets__item"
                    : cn("hbs-global-visually-hidden", "hbs-facets__item")
                }
                key={item.value}
              >
                <Checkbox.Root
                  className="hbs-facets__checkbox"
                  id={`facet-${i + 1}-${facetsID}`}
                  checked={item.checked}
                  onCheckedChange={(checked) => onCheckedChange(checked, item)}
                  ref={i === 0 ? itemsRef : null}
                >
                  <Checkbox.Indicator>
                    <Icon name="check" className="hbs-facets__check-icon" />
                  </Checkbox.Indicator>
                  <span className="hbs-global-visually-hidden">
                    Filter by {item.name} {item.number}
                  </span>
                </Checkbox.Root>
                <label
                  aria-hidden="true"
                  className="hbs-facets__label"
                  htmlFor={`facet-${i + 1}-${facetsID}`}
                >
                  {item.name}{" "}
                  {item.number && (
                    <span className="hbs-facets__number">{item.number}</span>
                  )}
                </label>
              </div>
            ))}
            <div className="hbs-facets__item">
              {truncatedItems.length < dynamicLength(letter) && (
                <CtaLink
                  className="hbs-facets__more-button"
                  isSmall
                  hideIcon
                  onClick={() => setFocusAndLength()}
                  ref={moreRef}
                  aria-label={`More ${title}`}
                >
                  More
                </CtaLink>
              )}
            </div>
          </div>
        </Collapsible.Content>
      </fieldset>
    </Collapsible.Root>
  );
}
